/** @format */

import PlanDurationSwitcher from "./PlanDurationSwitcher";
import { useSearchParams } from "react-router-dom";
import usePricing from "../../../hooks/usePricing";
import styles from "../../../styles/Components/pricing-plan/new-plans/PlanPayment.module.css";
import { Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  activateServiceWithCrypto,
  getCheckoutLink,
} from "../../../redux/pricing-plan/action";
import { useEffect, useRef } from "react";
import useServiceCost from "../../../hooks/useServiceCost";
import useUserData from "../../../hooks/useUserData";

function PlanPayment({ plan }) {
  const {
    main: { userData },
    pricingPlan: {
      checkoutLoading,
      checkoutSession,
      checkoutError,
      activateServiceWithCryptoResponse,
      activateServiceWithCryptoStatus: cryptoCheckoutStatus,
    },
  } = useSelector((state) => state);

  const { username, email } = useUserData();

  const controllerRef = useRef(null);
  const cryptoCheckoutControllerRef = useRef(null);

  const abortPreviousRequests = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
  };

  const [searchParams] = useSearchParams();
  const duration = searchParams.get("duration") || "annual";

  const {
    basicPlanMonthlyCost,
    proPlanMonthlyCost,
    proYearlyRegularCost,
    proYearlyTotalCost,
    basicYearlyRegularCost,
    basicYearlyTotalCost,
  } = usePricing();

  const dispatch = useDispatch();

  const { finalPrice, activationMode } = useServiceCost({
    isLiteSubscription: plan === "Lite",
    isProSubscription: plan === "Pro",
    duration: duration === "annual" ? 12 : 1,
  });

  const regularPrice =
    plan === "Lite" ? basicYearlyRegularCost : proYearlyRegularCost;
  const totalPrice =
    plan === "Lite"
      ? duration === "monthly"
        ? basicPlanMonthlyCost
        : basicYearlyTotalCost
      : duration === "annual"
        ? proYearlyTotalCost
        : proPlanMonthlyCost;

  const annualDiscount = regularPrice - totalPrice;
  const discountPercentage = Math.round((annualDiscount / regularPrice) * 100);

  const handlePayment = () => {
    if (checkoutLoading || checkoutError) return;
    // window.location.href = checkoutSession;
    window.open(checkoutSession, "_self");
  };

  const handleRefreshCheckoutSession = (selectedDuration) => {
    if (checkoutLoading) {
      abortPreviousRequests();
    }

    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;

    const redirectUrl = window.location.href;
    const selectedPlan =
      plan === "Lite"
        ? selectedDuration === "monthly"
          ? "monthly"
          : "yearly"
        : plan === "Pro"
          ? selectedDuration === "monthly"
            ? "monthly_pro"
            : "yearly_pro"
          : null;

    dispatch(
      getCheckoutLink(
        userData["cognito:username"],
        userData?.["custom:custom_username"],
        selectedPlan,
        redirectUrl,
        redirectUrl,
        signal,
      ),
    );
  };

  const handlePaymentWithCrypto = () => {
    window.open(
      `${activateServiceWithCryptoResponse?.checkout_session_url?.data?.invoice_url}`,
      "_self",
    );
  };

  const abortCryptoCheckoutRequest = () => {
    if (cryptoCheckoutControllerRef.current) {
      cryptoCheckoutControllerRef.current.abort();
    }
  };

  useEffect(() => {
    if (username) {
      if (activationMode === "activate_") return;
      abortCryptoCheckoutRequest();

      const controller = new AbortController();
      cryptoCheckoutControllerRef.current = controller;
      const { signal } = controller;

      const redirectUrl = window.location.href;

      dispatch(
        activateServiceWithCrypto(
          username,
          email,
          activationMode,
          `${finalPrice}`,
          redirectUrl,
          redirectUrl,
          signal,
        ),
      );
      return () => {
        controller.abort();
      };
    }
  }, [activationMode, dispatch, email, finalPrice, username]);

  return (
    <div className={styles.container}>
      <PlanDurationSwitcher
        style={{ width: "fit-content", margin: "0 auto" }}
        className={styles.switcherContainer}
        onChange={handleRefreshCheckoutSession}
      />
      <div className={styles.costContainer}>
        {duration === "annual" && (
          <>
            <div className={`${styles.costInfo}`}>
              <span>Regular Price:</span>
              <span>${regularPrice}</span>
            </div>
            <div className={`${styles.costInfo} ${styles.discountPrice}`}>
              <span>
                Annual Discount
                <span className={styles.discountBadge}>
                  -{discountPercentage}%
                </span>
              </span>
              <span>-${Math.round(annualDiscount * 100) / 100}</span>
            </div>
          </>
        )}
        <div className={`${styles.costInfo}`}>
          <strong>Total Price:</strong>
          <strong>${Math.round(totalPrice * 100) / 100}</strong>
        </div>
      </div>
      <div className={styles.checkoutBtns}>
        <div className={styles.checkoutBtn}>
          <Button
            loading={checkoutLoading}
            disabled={checkoutLoading || checkoutError || !checkoutSession}
            onClick={handlePayment}
            type="primary"
          >
            {checkoutLoading
              ? "Processing Card Payment"
              : "Pay with Card (Auto Renewal)"}
          </Button>
          {checkoutError && (
            <span>Something went wrong! Please try again later.</span>
          )}
        </div>
        <div className={styles.checkoutBtn}>
          <Button
            loading={cryptoCheckoutStatus === "loading"}
            disabled={
              cryptoCheckoutStatus === "loading" ||
              activateServiceWithCryptoResponse?.can_proceed === "False" ||
              cryptoCheckoutStatus === "error" ||
              activateServiceWithCryptoResponse?.checkout_session_url
                ?.status === "error"
            }
            onClick={handlePaymentWithCrypto}
            type="primary"
          >
            {cryptoCheckoutStatus === "loading"
              ? "Processing Crypto Payment"
              : `Pay with Crypto (One ${duration === "annual" ? "Year" : "Month"} Access)`}
          </Button>
          {(cryptoCheckoutStatus === "error" ||
            activateServiceWithCryptoResponse?.checkout_session_url?.status ===
              "error") && (
            <span>Something went wrong! Please try again later.</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default PlanPayment;
